import React, { useState } from 'react'
import { Card, Button, Spinner } from 'react-bootstrap'
import { Doughnut, Bar } from 'react-chartjs-2'
import { useAppSelector, useClassDistribution } from '../store/hooks'
import 'chart.js/auto'
import { selectSelectedProjectId } from '../store/slices/projectSlice'
import { selectDatasetSources } from '../store/slices/datasetSlice'
import { TooltipItem } from 'chart.js'
import { FaChartBar, FaChartPie } from 'react-icons/fa'
import { useFetchProjectsQuery } from '../services/projectsApi'

interface ClassDistributionCardProps {
  title?: string
  showButtons?: boolean
  useSources?: string[]
  height?: string
  datasetId?: string
  unstyled?: boolean
}

const ClassDistributionCard: React.FC<ClassDistributionCardProps> = ({
  title = 'Class Distribution',
  showButtons = true,
  useSources,
  height = '600px',
  datasetId,
  unstyled = false,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: projectsData } = useFetchProjectsQuery({}, { skip: !selectedProjectId })
  const project = projectsData?.projects?.find((p) => p.id === selectedProjectId)

  const datasetSources = useAppSelector(selectDatasetSources)

  const [viewMode, setViewMode] = useState<'bar' | 'doughnut' | 'radar'>('bar')

  const effectiveDatasetId = datasetId || project?.datasetid || ''
  const effectiveSources = useSources || (datasetId ? undefined : datasetSources)

  const classDistribution = useClassDistribution(
    selectedProjectId || '',
    effectiveDatasetId,
    effectiveSources
  )

  // Early return with loading state if we don't have required data
  if (!selectedProjectId || (!datasetId && !project?.datasetid)) {
    return unstyled ? (
      <div className="d-flex justify-content-center align-items-center" style={{ height }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    ) : (
      <Card className="class-distribution-card mt-2 shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center text-white p-2">
          <h5 className="mb-0 card-title">{title}</h5>
        </Card.Header>
        <Card.Body className="p-3 d-flex justify-content-center align-items-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Card.Body>
      </Card>
    )
  }

  // Only calculate these if we have data
  const maxValue =
    classDistribution.length > 0 ? Math.max(...classDistribution.map((item) => item.value)) : 0
  const maxRange = Math.ceil(maxValue / 100) * 100 + 100

  const doughnutData = {
    labels: classDistribution.map((item) => item.name),
    datasets: [
      {
        data: classDistribution.map((item) => item.value),
        backgroundColor: classDistribution.map((item) => item.color),
      },
    ],
  }

  const barData = {
    labels: classDistribution.map((item) => item.name),
    datasets: [
      {
        label: '',
        data: classDistribution.map((item) => item.value),
        backgroundColor: classDistribution.map((item) => item.color),
        barThickness: 20,
        maxBarThickness: 20,
        minBarLength: 2,
      },
    ],
  }

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        beginAtZero: true,
        barPercentage: 0.9,
        categoryPercentage: 0.9,
        grid: {
          display: false,
        },
        ticks: {
          color: '#fff',
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
          padding: 12,
          font: {
            size: 14,
          },
        },
      },
      y: {
        beginAtZero: true,
        max: maxRange,
        ticks: {
          color: '#fff',
          font: {
            size: 14,
          },
        },
      },
    },
    layout: {
      padding: {
        left: 24,
        right: 24,
        top: 24,
        bottom: 0,
      },
    },
    plugins: {
      legend: {
        display: viewMode !== 'bar',
        labels: {
          color: '#fff',
          font: {
            size: 16,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<never>) {
            const label = context.label
            const value = context.raw
            return `${label}: ${value}`
          },
        },
      },
    },
  }

  if (unstyled) {
    return (
      <div className="chart-container" style={{ height }}>
        {viewMode === 'bar' && <Bar data={barData} options={chartOptions} />}
        {viewMode === 'doughnut' && <Doughnut data={doughnutData} options={chartOptions} />}
      </div>
    )
  }

  return (
    <Card className="class-distribution-card mt-2 shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center text-white p-2">
        <h5 className="mb-0 card-title">{title}</h5>
        {showButtons && (
          <div className="chart-type-buttons d-flex gap-1">
            <Button
              size="sm"
              variant={viewMode === 'bar' ? 'primary' : 'outline-light'}
              onClick={() => setViewMode('bar')}
              title="Bar Chart"
              className="chart-type-btn"
            >
              <FaChartBar />
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'doughnut' ? 'primary' : 'outline-light'}
              onClick={() => setViewMode('doughnut')}
              title="Doughnut Chart"
              className="chart-type-btn"
            >
              <FaChartPie />
            </Button>
          </div>
        )}
      </Card.Header>
      <Card.Body className="p-3">
        {viewMode === 'bar' && (
          <div className="chart-container mb-3" style={{ height }}>
            <Bar data={barData} options={chartOptions} />
          </div>
        )}
        {viewMode === 'doughnut' && (
          <div className="chart-container mb-3" style={{ height }}>
            <Doughnut data={doughnutData} options={chartOptions} />
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

export default ClassDistributionCard
